/* eslint-disable @typescript-eslint/no-explicit-any */
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import { closeAndUnCloseDealThunk } from '../../../redux/thunks/deal';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { DealCloseAndUnclosedParams } from '../../../params/pipeline';
import { emailSchema } from '../../../schema';
import { resetStatesExceptAuth } from '../../../helper/reset_helper';
import { setIsSales } from '../../../redux/slices';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';


/**
 * @heading1: CloseDealModal
 * @paragraph: A modal component for closing a deal by selecting a stage.
 * Provides a dropdown for choosing the stage and action buttons for confirmation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const CloseDealModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch:
     * loaders/selectedDeal: An object containing the details from the Redux store.
     */
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const { selectedDeal, loaders } = useSelector((state: RootState) => state.deal);
    const { selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const { isSales } = useSelector((state: RootState) => state.app);

    /**
     * heading3: Functions
     * @heading4: handleSubmit
     * @danger: Handles the submission logic for closing the deal.
     */
    const handleSubmit = async (values: any) => {
        const data: DealCloseAndUnclosedParams = {
            deal_id: selectedDeal?.id as number,
            is_closed: selectedDeal?.is_closed === 1 ? 0 : 1,
            email: selectedDeal?.email ? selectedDeal.email : values.email
        };
        try {
            const client_id = await dispatch(closeAndUnCloseDealThunk({ data, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            if (selectedDeal?.is_closed === 0 && !id) {
                await resetStatesExceptAuth(dispatch);
                dispatch(setIsSales(!isSales));
                navigate(`/app/client/${client_id}/dashboard`);
            }
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    if (selectedDeal?.email) {
        return (
            <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title={selectedDeal?.is_closed === 0 ? 'Close deal' : 'Unclose deal'}>
                {/* Label for the dropdown */}
                <Typography fontWeight="600" align="center" color="textSecondary">Are you sure you want to {selectedDeal?.is_closed === 0 ? 'close' : 'unclose'} this deal?</Typography>
                {/* Dropdown for selecting the stage */}
                <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
                    <Typography fontWeight={700} variant="body2" color="textSecondary" display="flex" alignItems="center">
                        <BusinessIcon sx={{ fontSize: 16, marginRight: 1 }} />
                        {selectedDeal?.company}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="textSecondary" display="flex" alignItems="center">
                        $ {selectedPipeline?.deal_default_value}
                    </Typography>
                </Box>
                {/* Action buttons */}
                <Box display="flex" gap="10px">
                    <Button variant="outlined" fullWidth onClick={onClose} disabled={loaders.isUpdating}>No</Button>
                    <Button variant="contained" fullWidth onClick={handleSubmit} disabled={loaders.isUpdating}>{loaders.isUpdating && selectedDeal?.is_closed === 1 ? 'Unclosing...' : loaders.isUpdating && selectedDeal?.is_closed === 0 ? 'Closing...' : 'Yes'}</Button>
                </Box>
            </CustomModal>
        );
    }
    return (
        <CustomModal open={open} handleClose={loaders.isUpdating ? () => null : onClose} title={selectedDeal?.is_closed === 0 ? 'Close deal' : 'Unclose deal'}>
            {/* Label for the dropdown */}
            <Typography fontWeight="600" align="center" color="textSecondary">Email is mandatory for closing the deal.</Typography>
            {/* Dropdown for selecting the stage */}
            <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
                <Typography fontWeight={700} variant="body2" color="textSecondary" display="flex" alignItems="center">
                    <BusinessIcon sx={{ fontSize: 16, marginRight: 1 }} />
                    {selectedDeal?.company}
                </Typography>
                <Typography variant="body2" fontWeight={700} color="textSecondary" display="flex" alignItems="center">
                    $ {selectedPipeline?.deal_default_value}
                </Typography>
            </Box>
            <Formik
                initialValues={{
                    email: ''
                }}
                validationSchema={emailSchema}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                    <Form>
                        <CustomInput
                            value={values.email}
                            onChange={handleChange('email')}
                            onBlur={handleBlur('email')}
                            error={touched.email && errors.email}
                            fullWidth
                            label="Email"
                            placeholder="Enter email"
                            variant="outlined"
                            disabled={loaders.isUpdating}
                            mandatoryFields={true}
                        />
                        {/* Action buttons */}
                        <Box display="flex" gap="10px" mt={2}>
                            <Button variant="outlined" fullWidth onClick={onClose} disabled={loaders.isUpdating}>No</Button>
                            <Button type="submit" variant="contained" fullWidth disabled={loaders.isUpdating}>{loaders.isUpdating && selectedDeal?.is_closed === 1 ? 'Unclosing...' : loaders.isUpdating && selectedDeal?.is_closed === 0 ? 'Closing...' : 'Yes'}</Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </CustomModal>
    );
};

export default CloseDealModal;